import { computed } from 'vue'
import { useRuntimeConfig } from '#app'
import type { AnyBubble, PlaceBubble, WebPageBubble } from '@/stores/interfaces'
import { log } from '@/composables/useLogger'

interface Photo {
  url: string
  width?: number
  height?: number
}

const isPlaceBubble = (bubble: AnyBubble): bubble is PlaceBubble => {
  return 'photos' in bubble && 'primaryType' in bubble
}

export const useMediaResources = (bubble: AnyBubble) => {
  const config = useRuntimeConfig()
  const dataStore = config.public.dataStore

  const getCoverPhoto = computed(() => {
    if (bubble.offline && bubble.offlineMediaResources?.cover_photo) {
      return `${dataStore}${bubble.offlineMediaResources.cover_photo}`
    }

    // Handle different bubble types that might have coverPhoto
    if ('coverPhoto' in bubble) {
      return (bubble as WebPageBubble | PlaceBubble).coverPhoto
    }

    return undefined
  })

  const getPhotos = computed(() => {
    log('Bubble:', bubble)
    log('Bubble offline status:', bubble.offline)
    log('Offline media resources:', bubble.offlineMediaResources)

    // If bubble is marked as offline and has offline resources, always use those
    if (bubble.offline && bubble.offlineMediaResources) {
      const offlinePhotos = Object.entries(bubble.offlineMediaResources)
        .filter(([key]) => key !== 'cover_photo')
        .map(
          ([_, value]): Photo => ({
            url: `${dataStore}${value}`,
          })
        )

      log('Offline photos:', offlinePhotos)
      return offlinePhotos
    }

    // Handle PlaceBubble photos
    if (isPlaceBubble(bubble) && bubble.photos) {
      const photos = Object.values(bubble.photos).map(
        (photo): Photo => ({
          url: photo.url,
          width: photo.width,
          height: photo.height,
        })
      )
      log('Place photos:', photos)
      return photos
    }

    return []
  })

  return {
    getCoverPhoto,
    getPhotos,
  }
}
